import React, { useEffect, useMemo, useState } from "react";
import toastr from "toastr";
import {
  Paper,
  Typography,
  CircularProgress,
  Checkbox,
  Grid,
  Chip,
} from "@material-ui/core";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
} from "@material-ui/data-grid";
import {
  makeGETRequestOnUserService,
  makePOSTRequestOnUserService,
} from "../../Api/api";

interface Fund {
  _id: string;
  name: string;
  isin: string;
  fund_category: string;
  sub_category: string;
  is_prime_fund: boolean;
}

const SmestRecommends: React.FC = () => {
  const [funds, setFunds] = useState<Fund[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [selectionModel, setSelectionModel] = useState<string[]>([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  const loadFunds = async () => {
    try {
      setLoading(true);

      const response: any =
        await makeGETRequestOnUserService(
          "/mutual_fund/get_schemes/"
        );

      setFunds(response?.data?.data || []);
    } catch (error) {
      toastr.error("Failed to load funds.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFunds();
  }, []);

  const totalPrimeFunds = useMemo(() => {
    return funds.filter(
      (item) => item.is_prime_fund
    ).length;
  }, [funds]);

  const togglePrimeFund = async (fund: Fund) => {
    try {
      setSavingId(fund.isin);

      const newValue = !fund.is_prime_fund;

      await makePOSTRequestOnUserService(
        "/mutual_fund/update_is_prime_fund_by_isin/",
        {
          isin: fund.isin,
          is_prime_fund: newValue,
        }
      );

      setFunds((prev) =>
        prev.map((item) =>
          item.isin === fund.isin
            ? {
              ...item,
              is_prime_fund: newValue,
            }
            : item
        )
      );

      toastr.success(
        `Fund ${newValue ? "marked as" : "removed from"
        } SMEST Recommends`
      );
    } catch (error) {
      toastr.error("Failed to update recommendation.");
    } finally {
      setSavingId(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "is_prime_fund",
      headerName: "Recommended",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <Checkbox
            color="primary"
            checked={Boolean(params.value)}
            disabled={savingId === params.row.isin}
            onChange={(e) => {
              e.stopPropagation();
              togglePrimeFund(params.row);
            }}
          />
        );
      },
    },
    {
      field: "isin",
      headerName: "ISIN",
      width: 180,
    },
    {
      field: "name",
      headerName: "Fund Name",
      flex: 1,
      minWidth: 300,
    },
    {
      field: "fund_category",
      headerName: "Category",
      width: 180,
      valueGetter: (params) =>
        params.row.fund_category || "-",
    },
    {
      field: "sub_category",
      headerName: "Sub Category",
      width: 220,
      valueGetter: (params) =>
        params.row.sub_category || "-",
    },
  ];

  if (loading) {
    return (
      <Grid
        container
        justify="center"
        style={{ padding: 50 }}
      >
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <>
      <Paper
        elevation={3}
        style={{
          padding: 20,
          marginBottom: 20,
          borderRadius: 12,
        }}
      >
        <Grid
          container
          justify="space-between"
          alignItems="center"
          spacing={2}
        >
          <Grid item xs={12} md={8}>
            <Typography variant="h6">
              SMEST Recommended Funds
            </Typography>

            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 10 }}
            >
              Recommended ISINs:
            </Typography>

            <div style={{ marginTop: 8 }}>
              {funds
                .filter((fund) => fund.is_prime_fund)
                .map((fund) => (
                  <Chip
                    key={fund.isin}
                    label={fund.isin}
                    color="primary"
                    size="small"
                    style={{
                      marginRight: 8,
                      marginBottom: 8,
                    }}
                  />
                ))}

              {totalPrimeFunds === 0 && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                >
                  No recommended funds selected.
                </Typography>
              )}
            </div>
          </Grid>

          <Grid item>
            <Chip
              color="primary"
              label={`Total Recommended: ${totalPrimeFunds}`}
              style={{
                fontWeight: 600,
                fontSize: 14,
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={3}
        style={{
          height: 700,
          width: "100%",
          borderRadius: 12,
        }}
      >
        <DataGrid
          rows={funds}
          columns={columns}
          getRowId={(row) => row.isin}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50, 100]}
          pagination
          disableSelectionOnClick
          disableColumnMenu
          checkboxSelection={false}
          selectionModel={[]}
          loading={loading}
          components={{
            Toolbar: GridToolbar,
          }}
        />
      </Paper>
    </>
  );
};

export default SmestRecommends;